import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react"
import {
  StyledInput,
  StyledLabel,
  StyledInputNumberFormItem,
  PickerPopupStyle,
  StyledPickerView,
  PickerSelectedMask,
  PickerViewContainer,
  PickerHeader,
  PickerHeaderTop,
  PickerHeaderBottom,
  DoneButton,
  DetailsTrailing,
  OneDecimalWheelPickerPopup,
} from "./styled"
import { roundByDecimalPlace } from "utils"

const PopupIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="37"
      height="4"
      viewBox="0 0 37 4"
      fill="rgba(255, 255, 255, 0.20)"
    >
      <rect
        x="0.5"
        width="36"
        height="4"
        rx="2"
        fill="rgba(255, 255, 255, 0.20)"
      />
    </svg>
  )
}

interface IProps {
  value: number
  minInteger?: number
  maxInteger?: number
  minDecimalAtMinInteger?: number
  maxDecimalAtMaxInteger?: number
  isError?: boolean
  disabled?: boolean
  onChange: (value: number) => void
  detailTrailing?: string
  label: string
  placeholder?: string
  defaultInteger?: number
  defaultDecimal?: number
}

function extractIntAndDecimalPart(num: number) {
  const [intPart, decimalPart] = num.toString().split(".")
  return {
    integer: parseInt(intPart),
    decimal: parseInt(decimalPart || "0"), // handles cases like 2.0
  }
}

const OneDecimalWheelPicker = (props: IProps) => {
  const {
    value,
    onChange,
    isError,
    minInteger = 0,
    maxInteger = 10000,
    minDecimalAtMinInteger = 0,
    maxDecimalAtMaxInteger = 0,
    label,
    disabled,
    placeholder,
    defaultInteger = 0,
  } = props

  let detailTrailing = value !== 0 && props.detailTrailing

  const { integer: valueInteger, decimal: valueDecimal } =
    extractIntAndDecimalPart(value)
  const [visible, setVisible] = useState(false)
  const [selectedInteger, setSelectedInteger] = useState(
    value === 0 ? defaultInteger : valueInteger,
  )

  const [selectedDecimal, setSelectedDecimal] = useState(valueDecimal)

  const integerOptions = Array.from(
    { length: maxInteger - minInteger + 1 },
    (_, index) => ({
      label: (minInteger + index).toString(),
      value: minInteger + index,
    }),
  )

  let decimalOptions = Array.from({ length: 10 }, (_, index) => ({
    label: index.toString(),
    value: index,
  }))

  if (selectedInteger === minInteger) {
    decimalOptions = decimalOptions.filter(
      (option) => option.value >= minDecimalAtMinInteger,
    )
  }
  if (selectedInteger === maxInteger) {
    decimalOptions = decimalOptions.filter(
      (option) => option.value <= maxDecimalAtMaxInteger,
    )
  }

  useEffect(() => {
    if (selectedInteger === minInteger && selectedDecimal < minDecimalAtMinInteger) {
      setSelectedDecimal(minDecimalAtMinInteger)
    }
    if (selectedInteger === maxInteger && selectedDecimal > maxDecimalAtMaxInteger) {
      setSelectedDecimal(maxDecimalAtMaxInteger)
    }
  }, [selectedInteger])

  useEffect(() => {
    if (value === 0) {
      setSelectedInteger(defaultInteger)
      setSelectedDecimal(0)
      return
    }

    const isBelowMin =
      valueInteger < minInteger ||
      (valueInteger === minInteger && valueDecimal < minDecimalAtMinInteger)

    if (isBelowMin) {
      setSelectedInteger(minInteger)
      setSelectedDecimal(minDecimalAtMinInteger)
      return
    }

    const isAboveMax =
      valueInteger > maxInteger ||
      (valueInteger === maxInteger && valueDecimal > maxDecimalAtMaxInteger)

    if (isAboveMax) {
      setSelectedInteger(maxInteger)
      setSelectedDecimal(maxDecimalAtMaxInteger)
      return
    }

    if (valueInteger !== selectedInteger) {
      setSelectedInteger(valueInteger)
    }

    if (valueDecimal !== selectedDecimal) {
      setSelectedDecimal(valueDecimal)
    }
  }, [valueInteger, valueDecimal, value, defaultInteger])

  const trailingRef = useRef<HTMLDivElement>(null)
  const [trailingWidth, setTrailWidth] = useState(0)

  useLayoutEffect(() => {
    setTrailWidth(trailingRef.current?.getBoundingClientRect().width ?? 0)
  }, [trailingRef, detailTrailing])

  return (
    <Fragment>
      <PickerPopupStyle />
      <OneDecimalWheelPickerPopup
        className="one-decimal-picker-popup"
        onClose={() => setVisible(false)}
        closeOnMaskClick={true}
        visible={visible}
        bodyStyle={{
          borderRadius: "12px 12px 0 0",
        }}
      >
        <PickerHeader>
          <PickerHeaderTop>
            <PopupIcon />
          </PickerHeaderTop>
          <PickerHeaderBottom>
            <DoneButton
              onClick={() => {
                if (!disabled) {
                  onChange(
                    roundByDecimalPlace(
                      selectedInteger + selectedDecimal / 10,
                      1,
                    ),
                  )
                  setVisible(false)
                }
              }}
            >
              Done
            </DoneButton>
          </PickerHeaderBottom>
        </PickerHeader>

        <PickerViewContainer>
          <StyledPickerView
            columns={[integerOptions]}
            value={[selectedInteger]}
            onChange={(value) => {
              setSelectedInteger(value[0] as number)
            }}
          />

          <StyledPickerView
            columns={[decimalOptions]}
            value={[selectedDecimal]}
            onChange={(value) => {
              setSelectedDecimal(value[0] as number)
            }}
          />

          <PickerSelectedMask />
        </PickerViewContainer>
      </OneDecimalWheelPickerPopup>

      <StyledInputNumberFormItem
        style={
          {
            "--input-padding-right": detailTrailing ? "16px" : "16px",
            "--input-trailing-padding-left": detailTrailing ? "4px" : "0px",
            "--detail-trailing-width": `${trailingWidth}px`,
          } as React.CSSProperties
        }
      >
        <StyledLabel>{label}</StyledLabel>

        <StyledInput
          placeholder={placeholder}
          $disabled={disabled}
          $isError={!!isError}
          onClick={(e) => {
            if (disabled) return

            e.preventDefault()
            e.stopPropagation()
            setVisible(true)
          }}
          onFocus={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          value={value !== 0 ? value : ""}
          onChange={(e) => {}}
          style={{
            paddingRight:
              "calc(var(--input-padding-right) + var(--input-trailing-padding-left) + var(--detail-trailing-width))",
          }}
          readOnly={true}
        />

        {value !== 0 && (
          <Fragment>
            {detailTrailing && (
              <DetailsTrailing>{detailTrailing}</DetailsTrailing>
            )}

            {detailTrailing && (
              <DetailsTrailing
                style={{ visibility: "hidden" }}
                ref={trailingRef}
              >
                {detailTrailing}
              </DetailsTrailing>
            )}
          </Fragment>
        )}
      </StyledInputNumberFormItem>
    </Fragment>
  )
}

export default OneDecimalWheelPicker
